import React from 'react';
import './dropdownMenuCompact.scss';
import DropdownMenuCompact, {DropdownMenuCompactMenuPosition} from './DropdownMenuCompact';
import {Font} from '../../presentationTheme';
import Button, {ButtonColorType} from '../button/Button';

export interface BaseDropdownMenuCompactTileProps {
    icon?: string | JSX.Element;
    badge?: number;
    label: string;
    description?: string;
    disabled?: boolean;
    getDisabled?: () => boolean;
    level?: number;
    setDropdownOpen?: (value: boolean) => void;
    dataCy?: string;
    position?: DropdownMenuCompactMenuPosition;
    font?: Font;
    active?: boolean;
    color?: 'error' | 'success';
    shortcuts?: string[];
}

interface DropdownMenuCompactTileWithItems extends BaseDropdownMenuCompactTileProps {
    badge?: never;
    items: DropdownMenuCompactTileProps[][];
    onClick?: never;
    switchChecked?: never;
    onSwitch?: never;
    color?: never;
    shortcuts?: never;
}

export interface DropdownMenuCompactTileWithOnClick extends BaseDropdownMenuCompactTileProps {
    onClick: () => void;
    items?: never;
}

export type DropdownMenuCompactTileProps = DropdownMenuCompactTileWithItems | DropdownMenuCompactTileWithOnClick;

function DropdownMenuCompactTile({
    icon,
    badge,
    label,
    description,
    onClick,
    disabled,
    items,
    level,
    setDropdownOpen,
    dataCy,
    position = DropdownMenuCompactMenuPosition.RIGHT,
    font,
    active,
    color,
    shortcuts,
}: DropdownMenuCompactTileProps): JSX.Element {
    const handleLeafClick = (): void => {
        onClick?.();
        setDropdownOpen?.(false);
    };

    const sharedButtonProps = {
        dataCy,
        className: 'c-dropdownMenuCompactTile__button',
        size: 'sm' as const,
        fullWidth: true,
        color: (color ?? 'frost') as ButtonColorType,
        align: 'left' as const,
        variant: 'ghost' as const,
        disabled,
    };

    const item = description ? (
        <div className="c-dropdownMenuCompactTileContentWithDescription">
            <div className="c-dropdownMenuCompactTileContentWithDescription__label">{label}</div>
            <div className="c-dropdownMenuCompactTileContentWithDescription__description">{description}</div>
        </div>
    ) : (
        label
    );

    return items ? (
        <DropdownMenuCompact
            closeParent={() => setDropdownOpen?.(false)}
            menuPosition={position}
            items={items}
            level={level}
            width="fit"
            openOnMouseEnter
            closeOnMouseExit
        >
            <Button {...sharedButtonProps} leading={typeof icon === 'string' ? icon : undefined} trailing="ph:caret-right">
                {item}
            </Button>
        </DropdownMenuCompact>
    ) : (
        <Button
            {...sharedButtonProps}
            fontFamily={font?.name}
            onClick={handleLeafClick}
            leading={typeof icon === 'string' ? icon : undefined}
            trailing={active ? 'ph:check-bold' : undefined}
            badge={badge}
            type="button"
        >
            <div className="c-dropdownMenuCompactTile__button_content">
                {item}
                {shortcuts && (
                    <div className="c-dropdownMenuCompactTile__shortcuts">
                        {shortcuts.map((shortcut, index) => (
                            <span key={index} className="c-dropdownMenuCompactTile__shortcuts_item">
                                {shortcut.toUpperCase()}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </Button>
    );
}

export default DropdownMenuCompactTile;
